import IconCloud from '@apollo/icons/default/IconCloud.svg';
import React, { useContext } from 'react';

import * as orgRoutes from 'src/app/account/routes';
import { parseGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { CLOUD_ROUTER_ENDPOINT_DOMAIN_NAME } from 'src/app/onboarding/constants';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { Yup } from 'src/lib/yup';

import { FlowTypeContext } from '../FlowTypeProvider';
import {
  getRouterEndpoint,
  newSupergraphOnboardingValidationSchema,
} from '../shared';
type FormValues = Yup.InferType<typeof newSupergraphOnboardingValidationSchema>;

export const DedicatedVariantContent = ({ values }: { values: FormValues }) => {
  const { flowType } = useContext(FlowTypeContext);
  const { prefillGraphRef } = useRouteParams(orgRoutes.supergraphOnboarding);
  const graphRef = parseGraphRef(prefillGraphRef);

  return (
    <>
      {/* Graph Section */}
      <div className="mb-8">
        <div className="text-lg font-semibold">Supergraph Name</div>
        <span className="text-base font-semibold text-secondary">
          {values.supergraphName}
        </span>
      </div>
      {/* End of Graph Section */}
      {/* Graph Ref Section */}
      <div className="mb-8">
        <div className="text-lg font-semibold">Full Graph Reference</div>
        <span className="text-base font-semibold text-secondary">
          {graphRef?.graphId}@{values.variantName}
        </span>
      </div>
      {/* End of Graph Ref Section */}
      {/* Variant Section */}
      <div className="mb-8">
        <div className="text-lg font-semibold">Graph Variant</div>
        <span className="text-base font-semibold text-secondary">
          {values.variantName}
        </span>
      </div>
      {/* End of Variant Section */}
      {/* Router URL Section */}
      <div className="mb-8">
        <div className="text-lg font-semibold">Router Url</div>
        <div className="my-2 mb-4 flex min-h-12 items-center rounded-md border  border-silver bg-silver-lighter px-4 py-2 text-base text-secondary">
          <IconCloud className="mr-2 mt-1 size-4 self-start text-secondary" />
          {flowType === 'DEDICATED_PRIVATE' ? (
            <>{values.unregisteredEndpointUrl}</>
          ) : (
            <RouterEndpoint
              variantName={values.variantName}
              supergraphID={graphRef?.graphId ?? ''}
            />
          )}
        </div>
      </div>
      {/* End of Router URL Section */}
      {/* Provider and Region Section */}
      <div className="mb-8">
        <div className="text-lg font-semibold">Router Region</div>
        <div className="my-2 mb-4 flex min-h-12 items-center rounded-md border  border-silver bg-silver-lighter px-4 py-2 text-base text-secondary">
          {values.cloudRegion}
        </div>
        <div className="text-lg font-semibold">Cloud Provider</div>
        <div className="my-2 mb-4 flex min-h-12 items-center rounded-md border  border-silver bg-silver-lighter px-4 py-2 text-base text-secondary">
          {values.cloudProvider}
        </div>
      </div>
      {/* End of Provider and Region Section */}
    </>
  );
};

// TODO: MOVE THIS TO A SHARED FILE
const RouterEndpointDOM = ({
  url,
  variantName,
  supergraphID,
}: {
  url: URL;
  variantName: string;
  supergraphID: string;
}) => {
  const [host0, host1] = url.host.split('--');
  return (
    <div>
      {`${url.protocol}//`}
      <span className="font-semibold">{host0 ?? variantName}</span>
      {`--${host1 ?? `${supergraphID}.${CLOUD_ROUTER_ENDPOINT_DOMAIN_NAME}`}`}
    </div>
  );
};

export const RouterEndpoint = ({
  variantName,
  supergraphID,
}: {
  variantName: string;
  supergraphID: string;
}) => {
  const routerEndpointString = getRouterEndpoint({
    variantName,
    supergraphID,
  });
  try {
    const routerEndpointUrl = new URL(routerEndpointString);
    return (
      <RouterEndpointDOM
        url={routerEndpointUrl}
        variantName={variantName}
        supergraphID={supergraphID}
      />
    );
  } catch {
    return (
      <RouterEndpointDOM
        // if the router endpoint url parsing fails, the variant name is invalid as a URL.
        // fall back to it as an empty string
        url={new URL(getRouterEndpoint({ variantName: '', supergraphID }))}
        variantName=""
        supergraphID={supergraphID}
      />
    );
  }
};
